/**
 * Archive hook run tracking keyed by threadId.
 *
 * Receives {@link ArchiveHookEvent} pushes from the server's
 * `workspace.archiveHookStatus` WS channel and exposes them to the UI
 * (toasts + read-only output modal).
 */
import { create } from "zustand";

import type { ArchiveHookEvent, ArchiveHookStepResult, ThreadId } from "@taskin-code/contracts";

export interface ArchiveHookRun {
  readonly threadId: ThreadId;
  readonly projectId: string;
  readonly worktreePath: string;
  readonly totalSteps: number;
  readonly startedAt: string;
  finishedAt: string | null;
  phase: ArchiveHookEvent["phase"];
  failedStepIndex: number | null;
  steps: ArchiveHookStepResult[];
  toastId: string | null;
}

interface ArchiveHookStore {
  readonly runs: Record<string, ArchiveHookRun>;
  readonly applyEvent: (event: ArchiveHookEvent) => void;
  readonly setToastId: (threadId: ThreadId, toastId: string | null) => void;
  readonly clear: (threadId: ThreadId) => void;
}

export const useArchiveHookStore = create<ArchiveHookStore>((set) => ({
  runs: {},
  applyEvent: (event) =>
    set((state) => {
      const key = event.threadId as unknown as string;
      const existing = state.runs[key];
      const base: ArchiveHookRun = existing ?? {
        threadId: event.threadId,
        projectId: event.projectId,
        worktreePath: event.worktreePath,
        totalSteps: event.totalSteps,
        startedAt: event.startedAt,
        finishedAt: null,
        phase: event.phase,
        failedStepIndex: null,
        steps: [],
        toastId: null,
      };

      const next: ArchiveHookRun = {
        ...base,
        phase: event.phase,
        totalSteps: event.totalSteps,
      };

      if (event.phase === "step-finished" && event.result) {
        const steps = next.steps.filter((step) => step.stepIndex !== event.result!.stepIndex);
        steps.push(event.result);
        steps.sort((a, b) => a.stepIndex - b.stepIndex);
        next.steps = steps;
      }

      if (event.phase === "error") {
        next.failedStepIndex = event.failedStepIndex ?? null;
        next.finishedAt = event.occurredAt;
      }

      if (event.phase === "completed") {
        next.finishedAt = event.occurredAt;
      }

      return {
        runs: { ...state.runs, [key]: next },
      };
    }),
  setToastId: (threadId, toastId) =>
    set((state) => {
      const key = threadId as unknown as string;
      const run = state.runs[key];
      if (!run) return state;
      return { runs: { ...state.runs, [key]: { ...run, toastId } } };
    }),
  clear: (threadId) =>
    set((state) => {
      const key = threadId as unknown as string;
      if (!(key in state.runs)) return state;
      const next = { ...state.runs };
      delete next[key];
      return { runs: next };
    }),
}));

export const selectArchiveHookRun =
  (threadId: ThreadId) =>
  (state: { runs: Record<string, ArchiveHookRun> }): ArchiveHookRun | null =>
    state.runs[threadId as unknown as string] ?? null;
